import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import { useCart } from '../context/CartContext';
import { categoriesApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Search, X, Check } from 'lucide-react';
import CommitmentSection from '../components/CommitmentSection';
import { optimizeImageUrl } from '../utils/imageOptimizer';

// Product Card Component with Variant Selection
const ProductCard = ({ product, addToCart }) => {
  const [selectedVariant, setSelectedVariant] = useState(product.variants?.[0] || null);
  
  const handleAddToCart = () => {
    if (selectedVariant) {
      addToCart(product, selectedVariant, 1);
    }
  };

  return (
    <div className="group">
      <Link to={`/products/${product.slug}`} className="block">
        <div className="aspect-square bg-gray-50 overflow-hidden mb-4 relative">
          <img
            src={optimizeImageUrl(product.images?.[0], { width: 400 }) || 'https://via.placeholder.com/400'}
            alt="Natural and organic skincare product"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            width={400}
            height={400}
            loading="lazy"
          />
          {product.isNew && (
            <span className="absolute top-3 left-3 bg-[#2d4a47] text-white text-xs px-2 py-1 uppercase tracking-wider">
              New
            </span>
          )}
          {product.onSale && (
            <span className="absolute top-3 right-3 bg-red-500 text-white text-xs px-2 py-1 uppercase tracking-wider">
              Sale
            </span>
          )}
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">{product.name}</h3>
        <p className="text-sm text-gray-500 mb-2">{product.category}</p>
      </Link>
      
      {/* Variant Selection */}
      {product.variants && product.variants.length > 1 && (
        <div className="mb-3">
          <p className="text-xs text-gray-500 mb-2">Select Size:</p>
          <div className="flex flex-wrap gap-2">
            {product.variants.map((variant) => (
              <button
                key={variant.id || variant.sku}
                onClick={() => setSelectedVariant(variant)}
                className={`px-3 py-1.5 text-sm border transition-colors ${
                  selectedVariant?.id === variant.id || selectedVariant?.sku === variant.sku
                    ? 'bg-[#2d4a47] text-white border-[#2d4a47]'
                    : 'border-gray-300 hover:border-[#2d4a47] text-gray-700'
                }`}
              >
                {variant.size}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Price Display */}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-gray-900 font-medium">
          ${(selectedVariant?.price ?? product.variants?.[0]?.price ?? 0).toFixed(2)}
        </span>
        {(selectedVariant?.comparePrice || product.variants?.[0]?.comparePrice) > 0 && 
         (selectedVariant?.comparePrice || product.variants?.[0]?.comparePrice) > (selectedVariant?.price || product.variants?.[0]?.price) && (
          <span className="text-gray-400 line-through text-sm">
            ${selectedVariant.comparePrice.toFixed(2)}
          </span>
        )}
      </div>
      
      <Button
        onClick={handleAddToCart}
        className="w-full bg-[#2d4a47] hover:bg-[#1e3633] text-white rounded-none"
      >
        Add to Cart {selectedVariant?.size ? `- ${selectedVariant.size}` : ''}
      </Button>
    </div>
  );
};

const ProductsPage = () => {
  const { siteData } = useSite();
  const { addToCart } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await categoriesApi.getAll();
        setCategories(res.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Update search query from URL params
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchQuery(urlSearch);
    }
  }, [searchParams]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setSearchQuery(''); // Clear search when changing category
    if (categoryId) {
      setSearchParams({ category: categoryId });
    } else {
      setSearchParams({});
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchParams({});
  };

  // Filter products by category
  const filteredProducts = selectedCategory
    ? siteData.products.filter(p => p.categoryId === selectedCategory)
    : siteData.products;

  // Also filter by sale/new if those params exist
  const saleFilter = searchParams.get('sale');
  const newFilter = searchParams.get('new');
  
  let displayProducts = filteredProducts;
  if (saleFilter === 'true') {
    displayProducts = filteredProducts.filter(p => p.onSale);
  } else if (newFilter === 'true') {
    displayProducts = filteredProducts.filter(p => p.isNew);
  }

  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    displayProducts = displayProducts.filter(product =>
      product.name?.toLowerCase().includes(query) ||
      product.description?.toLowerCase().includes(query) ||
      product.category?.toLowerCase().includes(query) ||
      product.shortDescription?.toLowerCase().includes(query) ||
      product.shortName?.toLowerCase().includes(query)
    );
  }

  // Determine page title
  const getPageTitle = () => {
    if (searchQuery.trim()) {
      return `Search results for "${searchQuery}"`;
    }
    if (saleFilter === 'true') return 'On Sale';
    if (newFilter === 'true') return 'New Arrivals';
    return 'Our Products';
  };

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-serif text-center mb-8">
          {getPageTitle()}
        </h1>

        {/* Search indicator */}
        {searchQuery.trim() && (
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#f5f1eb] px-4 py-2 rounded-full">
              <Search size={16} className="text-[#2d4a47]" />
              <span className="text-sm text-gray-700">
                {displayProducts.length} result{displayProducts.length !== 1 ? 's' : ''} for "{searchQuery}"
              </span>
              <button
                onClick={clearSearch}
                className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}
        
        {/* Category Filter */}
        {categories.length > 0 && !saleFilter && !newFilter && !searchQuery.trim() && (
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => handleCategoryChange('')}
              className={`px-4 py-2 text-sm border transition-colors ${
                !selectedCategory
                  ? 'bg-[#2d4a47] text-white border-[#2d4a47]'
                  : 'border-gray-300 hover:border-[#2d4a47]'
              }`}
            >
              All Products
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`px-4 py-2 text-sm border transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-[#2d4a47] text-white border-[#2d4a47]'
                    : 'border-gray-300 hover:border-[#2d4a47]'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        )}
        
        {displayProducts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No products found in this category.</p>
            <Button onClick={() => handleCategoryChange('')} variant="outline">
              View All Products
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayProducts.map((product) => (
              <ProductCard 
                key={product.id} 
                product={product} 
                addToCart={addToCart} 
              />
            ))}
          </div>
        )}

        {/* Commitment Section */}
        <CommitmentSection variant="box" />
      </div>
    </div>
  );
};

export default ProductsPage;
